import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Menu,
  Group,
  Avatar,
  Text,
  Divider,
  ActionIcon,
  Badge
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconLogin,
  IconLogout,
  IconUser,
  IconChevronDown,
  IconUserPlus
} from '@tabler/icons-react'
import { logoutUser } from '../../store/slices/authSlice'
import AuthModal from './AuthModal'

function AuthButton({ variant = 'default', size = 'md' }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth)
  const [authModalOpened, setAuthModalOpened] = useState(false)
  const [authModalMode, setAuthModalMode] = useState('login')
  const isMobile = useMediaQuery('(max-width: 48em)')

  const handleLogin = () => {
    setAuthModalMode('login')
    setAuthModalOpened(true)
  }

  const handleRegister = () => {
    setAuthModalMode('register')
    setAuthModalOpened(true)
  }

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser())
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleProfile = () => {
    navigate('/profile')
  }

  const closeAuthModal = () => {
    setAuthModalOpened(false)
  }

  // If user is authenticated, show user menu
  if (isAuthenticated && user) {
    // On mobile, show just avatar as ActionIcon
    if (isMobile) {
      return (
        <>
          <Menu shadow="md" width={250} position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant={variant === 'subtle' ? 'subtle' : 'light'}
                size="lg"
                loading={loading}
                disabled={loading}
              >
                <Avatar 
                  size="sm" 
                  src={user.avatar} 
                  alt={user.username}
                  color="blue"
                >
                  {user.username?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                {user.username || user.email}
                {user.is_staff && (
                  <Badge size="xs" color="orange" variant="light" ml="xs">
                    Staff
                  </Badge>
                )}
              </Menu.Label>
              <Menu.Item 
                leftSection={<IconUser size={16} />}
                onClick={handleProfile}
              >
                Profile
              </Menu.Item>
              
              <Divider />
              
              <Menu.Item
                leftSection={<IconLogout size={16} />}
                onClick={handleLogout}
                color="red"
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <AuthModal
            opened={authModalOpened}
            mode={authModalMode}
            onClose={closeAuthModal}
          />
        </>
      )
    }
    
    // Desktop view - show full button
    return (
      <>
        <Menu shadow="md" width={250} position="bottom-end">
          <Menu.Target>
            <Button
              variant={variant === 'subtle' ? 'subtle' : 'light'}
              size={size}
              leftSection={
                <Avatar 
                  size="sm" 
                  src={user.avatar} 
                  alt={user.username}
                  color="blue"
                >
                  {user.username?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              }
              rightSection={<IconChevronDown size={14} />}
              loading={loading}
              disabled={loading}
            >
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  {user.username || user.email}
                </Text>
                {user.is_staff && (
                  <Badge size="xs" color="orange" variant="light">
                    Staff
                  </Badge>
                )}
              </Group>
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Account</Menu.Label>
            <Menu.Item 
              leftSection={<IconUser size={16} />}
              onClick={handleProfile}
            >
              Profile
            </Menu.Item>
            
            <Divider />
            
            <Menu.Item
              leftSection={<IconLogout size={16} />}
              onClick={handleLogout}
              color="red"
            >
              Sign Out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <AuthModal
          opened={authModalOpened}
          onClose={closeAuthModal}
          initialMode={authModalMode}
        />
      </>
    )
  }

  // If user is not authenticated, show login/register buttons
  return (
    <>
      <Group gap="sm">
        <Button
          variant="subtle"
          size={size}
          leftSection={<IconLogin size={16} />}
          onClick={handleLogin}
          loading={loading}
          disabled={loading}
        >
          Sign In
        </Button>
        <Button
          variant="filled"
          size={size}
          leftSection={<IconUserPlus size={16} />}
          onClick={handleRegister}
          loading={loading}
          disabled={loading}
        >
          Sign Up
        </Button>
      </Group>

      <AuthModal
        opened={authModalOpened}
        onClose={closeAuthModal}
        initialMode={authModalMode}
      />
    </>
  )
}

export default AuthButton